/**
 * Per-platform staggered scheduling — pure helpers (Phase 3, Rev 2).
 *
 * A post's `platform_schedules` (jsonb) optionally gives each platform its own
 * send time + lifecycle. `null`/absent entry = that platform uses the row-level
 * `scheduled_at` and the row status — so the common (non-staggered) path and
 * every existing row are unchanged.
 *
 * These are the SINGLE source of truth for effective time, the row-status
 * rollup, and the due set, shared by the client and (mirrored) the dispatcher.
 * Kept pure + unit-tested; the reworked design (per-platform atomic claim,
 * partial jsonb writes) lives in the edge fn / RPCs, not here.
 *
 * Design: docs/superpowers/specs/2026-07-04-per-platform-scheduling-design.md
 */

import { platformOutcome } from '../../../shared/platform-outcome';
import { isValidPlatformPostId } from '../../../shared/platform-post-id';

export type PlatformSendStatus =
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'failed_connection_removed'
  /** Accepted by the platform but we hold no usable post id — never shown as published. */
  | 'unverified'
  /** This platform was never dispatched. Distinct from "pending". */
  | 'not_attempted';

export interface PlatformSchedule {
  scheduled_at: string;
  status: PlatformSendStatus;
  platform_post_id?: string;
  permalink?: string | null;
  error_message?: string;
  published_at?: string;
  retry_count?: number;
  last_retry_at?: string;
}

export type PlatformSchedulesMap = Record<string, PlatformSchedule>;

/** The row `status` values this module reasons about. */
export type RowScheduleStatus =
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'partially_scheduled'
  | 'partially_published'
  | 'failed'
  | 'failed_connection_removed';

const TERMINAL: ReadonlySet<PlatformSendStatus> = new Set([
  'published',
  'failed',
  'failed_connection_removed',
]);
const PENDING: ReadonlySet<PlatformSendStatus> = new Set(['scheduled', 'publishing']);

/** Narrow an untrusted jsonb value to a schedules map (drops malformed entries). */
export function parsePlatformSchedules(value: unknown): PlatformSchedulesMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: PlatformSchedulesMap = {};
  for (const [platform, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    if (typeof entry.scheduled_at !== 'string' || typeof entry.status !== 'string') continue;
    out[platform] = { ...(entry as unknown as PlatformSchedule) };
  }
  return out;
}

/**
 * Effective send time for a platform: its own per-platform time, else the
 * row-level `scheduled_at`. Returns null only when neither exists.
 */
export function effectiveSendTime(
  platform: string,
  schedules: PlatformSchedulesMap,
  rowScheduledAt: string | null,
): string | null {
  return schedules[platform]?.scheduled_at ?? rowScheduledAt ?? null;
}

/**
 * Effective per-platform status.
 *
 * ⚠️ Pass `platformResponse` wherever you have it. It is the ONLY authoritative
 * record of what each platform actually did, and without it this function has
 * to fall back to the row status — which is how a single-platform "Post now"
 * came to render "Facebook · published" and "LinkedIn · published" for
 * platforms that were never contacted. The edge function nulls
 * `platform_schedules` on every immediate publish, so on that path the fallback
 * was the ONLY thing driving the chips.
 *
 * With evidence supplied, a platform is `published` only with positive proof
 * (success + a real platform post id); everything else resolves to
 * `unverified`, `failed`, or `not_attempted`.
 */
export function effectivePlatformStatus(
  platform: string,
  schedules: PlatformSchedulesMap,
  rowStatus: string,
  platformResponse?: unknown,
): PlatformSendStatus {
  const explicit = schedules[platform]?.status;
  if (explicit) return explicit;

  if (platformResponse !== undefined && platformResponse !== null) {
    const { outcome } = platformOutcome(
      platform,
      platformResponse,
      schedules,
      isValidPlatformPostId,
    );
    switch (outcome) {
      case 'published':
        return 'published';
      case 'failed':
        return 'failed';
      case 'unverified':
        return 'unverified';
      case 'pending':
        return rowStatus === 'publishing' ? 'publishing' : 'scheduled';
      case 'not_attempted':
        // A row that is still in flight owes this platform an attempt; a
        // terminal row simply never sent it.
        return rowStatus === 'publishing' || rowStatus === 'scheduled'
          ? 'scheduled'
          : 'not_attempted';
    }
  }

  switch (rowStatus) {
    case 'published':
    case 'partially_published':
      return 'published';
    case 'failed':
      return 'failed';
    case 'failed_connection_removed':
      return 'failed_connection_removed';
    case 'publishing':
      return 'publishing';
    default:
      return 'scheduled';
  }
}

/** True when a platform has a distinct time from the row-level shared time. */
export function isStaggered(
  platform: string,
  schedules: PlatformSchedulesMap,
  rowScheduledAt: string | null,
): boolean {
  const own = schedules[platform]?.scheduled_at;
  return own != null && own !== rowScheduledAt;
}

/** True when ANY enabled platform has a distinct time (post is staggered). */
export function postIsStaggered(
  enabled: string[],
  schedules: PlatformSchedulesMap,
  rowScheduledAt: string | null,
): boolean {
  return enabled.some((p) => isStaggered(p, schedules, rowScheduledAt));
}

/**
 * The DUE SET at `now`: enabled platforms whose effective status is `scheduled`
 * and whose effective time ≤ now. This is what a dispatch pass fires (mirrored
 * server-side by the atomic claim). Paused/terminal platforms are excluded.
 */
export function dueSet(
  enabled: string[],
  schedules: PlatformSchedulesMap,
  rowScheduledAt: string | null,
  rowStatus: string,
  now: number,
): string[] {
  return enabled.filter((p) => {
    if (effectivePlatformStatus(p, schedules, rowStatus) !== 'scheduled') return false;
    const t = effectiveSendTime(p, schedules, rowScheduledAt);
    return t != null && new Date(t).getTime() <= now;
  });
}

/**
 * Row-status rollup from the enabled platforms' effective statuses. The
 * dispatcher writes this after each pass; the UI reads it for coarse state.
 *
 * - any enabled platform still pending AND none fired yet → `scheduled`
 * - any pending AND at least one already terminal → `partially_scheduled` (in-flight)
 * - all terminal, mix of published + failed → `partially_published`
 * - all published → `published`
 * - all failed → `failed` (or `failed_connection_removed` if that's the sole cause)
 *
 * Returns null when there are no enabled platforms (caller decides, e.g. draft).
 */
export function rollupRowStatus(
  enabled: string[],
  schedules: PlatformSchedulesMap,
  rowStatus: string,
): RowScheduleStatus | null {
  if (enabled.length === 0) return null;
  const statuses = enabled.map((p) => effectivePlatformStatus(p, schedules, rowStatus));
  const anyPending = statuses.some((s) => PENDING.has(s));
  const anyTerminal = statuses.some((s) => TERMINAL.has(s));
  const anyPublished = statuses.some((s) => s === 'published');

  if (anyPending) return anyTerminal ? 'partially_scheduled' : 'scheduled';
  // All terminal from here.
  if (anyPublished) {
    return statuses.every((s) => s === 'published') ? 'published' : 'partially_published';
  }
  // No publishes, all terminal → all failed.
  return statuses.every((s) => s === 'failed_connection_removed')
    ? 'failed_connection_removed'
    : 'failed';
}

/** True when the row has published at least one platform but still has pending work. */
export function isInFlightPartial(status: string): boolean {
  return status === 'partially_scheduled';
}

/** A published (or partially-published) platform's content is locked — it's live. */
export function isPlatformLocked(
  platform: string,
  schedules: PlatformSchedulesMap,
  rowStatus: string,
): boolean {
  const s = effectivePlatformStatus(platform, schedules, rowStatus);
  return s === 'published' || s === 'publishing';
}
